import { LitElement, html, css, PropertyValues, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
  resolveColor,
  isDefaultColorToken,
} from "../helper/utils/color-resolver";
import { cssOnFillEnhance, ON_COLOR_FALLBACK_LIGHT } from "../helper/utils/contrast-color";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { PresetController } from "../helper/controllers/preset-controller";

/**
 * @element sky-appbar
 *
 * @summary Application top bar with title, optional leading control, trailing actions, and optional color/image background.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/appbar
 * @dependency sky-icon
 *
 * @slot button - Leading area (e.g., menu/back button). Defaults to a hamburger icon.
 * @slot - Trailing actions (icons/buttons). This is the default slot.
 *
 * @property {string} title - Title text displayed in the app bar.
 * @property {string} color - Semantic token or CSS color for bar background; foreground color is derived for contrast.
 * @property {string} url - Background image URL.
 * @property {boolean} banner - Uses `role="banner"` when true, otherwise `role="group"`.
 * @property {boolean} dense - Enables compact vertical spacing.
 * @property {boolean} elevated - Applies elevated shadow styling.
 * @property {string} leadingLabel - Accessible label for default leading button fallback.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @csspart content - Wrapper that contains the leading slot, title, and actions.
 * @csspart leading - Container for the leading slot (e.g. menu button).
 * @csspart title - The title area (contains .title-text with the title string).
 * @csspart actions - The trailing actions container.
 *
 * @Behavior
 * - `color` resolves background via `resolveColor()`; solid FG uses on-color
 *   fallback + `contrast-color()` against the fill.
 * - `url` renders a background layer behind content.
 * - `banner` should be true only for the primary page app bar landmark.
 * - In RTL contexts, the default leading icon is mirrored unless reduced motion is enabled.
 *
 * @example
 * ```html
 * <sky-appbar title="Dashboard" color="primary" dense>
 *   <button slot="button">Menu</button>
 *   <sky-icon icon="ion:search"></sky-icon>
 * </sky-appbar>
 * ```
 * ```vue
 * <template>
 *   <sky-appbar title="Dashboard" color="primary" dense>
 *     <button slot="button">Menu</button>
 *     <sky-icon icon="ion:search"></sky-icon>
 *   </sky-appbar>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-appbar title="Dashboard" color="primary" dense>
 *       <button slot="button">Menu</button>
 *       <sky-icon icon="ion:search"></sky-icon>
 *     </sky-appbar>
 *   );
 * }
 * ```
 */
@customElement("sky-appbar")
export class SkyAppbar extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  /** Title text displayed in the app bar. @default "App Bar" */
  @property({ type: String }) title = "App Bar";

  /**
   * Bar background token or CSS color; when set, foreground uses on-color
   * fallback enhanced by `contrast-color()` against the fill.
   * @default "default"
   */
  @property({ type: String }) color = "default";

  /** Background image URL. @default "" */
  @property({ type: String }) url = "";

  /** When true, the bar has role="banner" (main app header). Set false for secondary bars. @default true */
  @property({ type: Boolean }) banner = true;

  /** When true, reduces vertical padding for a compact bar. @default false */
  @property({ type: Boolean }) dense = false;

  /** When true, applies stronger shadow (e.g. for sticky bar after scroll). @default false */
  @property({ type: Boolean }) elevated = false;

  /** Accessible label for the default leading button when no slot content is provided. @default "Open menu" */
  @property({ type: String }) leadingLabel = "Open menu";


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      font-family: var(--sky-font);
      overflow: hidden;
      color: var(--appbar-text-color, var(--sky-text-primary));
      transition: var(--sky-transition-default);
    }

    :host(:not([data-surface="solid"])) {
      background: var(--sky-glass-secondary);
      background-image: var(--sky-glass-surface);
      backdrop-filter:
        var(--sky-blur-secondary)
        var(--sky-saturation-secondary)
        var(--sky-brightness-secondary);
      -webkit-backdrop-filter:
        var(--sky-blur-secondary)
        var(--sky-saturation-secondary)
        var(--sky-brightness-secondary);
      border-bottom: var(--sky-border-secondary);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-primary);
    }

    :host([data-surface="solid"]) {
      background-color: var(--appbar-bg-color);
      color: var(--appbar-text-color);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border-bottom: var(--sky-border-primary);
      box-shadow: var(--sky-box-shadow-primary);
    }

    :host([elevated]:not([data-surface="solid"])) {
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-secondary);
    }

    :host([elevated][data-surface="solid"]) {
      box-shadow: var(--sky-box-shadow-secondary);
    }

    .background {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      background-image: var(--appbar-bg-image, none);
      background-size: cover;
      background-position: center;
      z-index: -2;
      pointer-events: none;
    }

    .background-overlay {
      position: absolute;
      inset: 0;
      background: var(--appbar-bg-overlay, transparent);
      z-index: -1;
      pointer-events: none;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .content {
      display: flex;
      padding: var(--sky-space-2, 8px) var(--sky-space-4, 16px);
      align-items: center;
      justify-content: space-between;
      width: 100%;
      position: relative;
      gap: var(--sky-space-2, 8px);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    :host([dense]) .content {
      padding-block: var(--sky-space-1, 4px);
    }

    .title-area {
      flex: 1 1 auto;
      min-width: 0;
      padding-inline-start: var(--sky-space-2\\.5, 10px);
    }

    .title-text {
      font: var(--sky-text-title);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .leading {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .leading ::slotted(*) {
      cursor: pointer;
    }

    .leading-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      padding: 0;
      border: none;
      background: none;
      cursor: pointer;
      font: inherit;
      color: inherit;
      -webkit-tap-highlight-color: transparent;
      min-width: var(--sky-touch-target, 44px);
      min-height: var(--sky-touch-target, 44px);
      box-sizing: border-box;
      border-radius: var(--sky-radius-primary);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition: var(--sky-transition-fast);
    }

    .leading-button:hover {
      background: var(--sky-hover-primary);
    }

    .leading-button:focus-visible,
    .leading ::slotted(*:focus-visible),
    .actions ::slotted(*:focus-visible) {
      outline: none;
      box-shadow: var(--sky-focus-ring);
    }

    .icon {
      cursor: pointer;
      font-size: var(--sky-font-size-xl, 1.25rem);
      display: flex;
      align-items: center;
    }

    .actions {
      display: flex;
      align-items: center;
      gap: var(--sky-space-2, 8px);
      padding-inline-start: var(--sky-space-2\\.5, 10px);
      flex-shrink: 0;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .actions ::slotted(button),
    .actions ::slotted([role="button"]) {
      min-width: var(--sky-touch-target, 44px);
      min-height: var(--sky-touch-target, 44px);
      box-sizing: border-box;
    }

    :host-context([dir="rtl"]) .leading-button .icon {
      transform: scaleX(-1);
    }

    @media (prefers-reduced-motion: reduce) {
      :host,
      .content,
      .leading-button,
      .leading ::slotted(*),
      .actions ::slotted(*) {
        transition: none;
      }

      :host-context([dir="rtl"]) .leading-button .icon {
        transform: none;
      }
    }

    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ':host([data-surface="solid"])',
          fillVar: "--appbar-bg-color",
        },
      ]),
    )}
  `;

  /** Applies glass vs solid surface tokens from `color`. */
  private _applyColorSurface(): void {
    if (!isDefaultColorToken(this.color)) {
      this.setAttribute("data-surface", "solid");
      this.style.setProperty("--appbar-bg-color", resolveColor(this.color));
      this.style.setProperty("--appbar-text-color", ON_COLOR_FALLBACK_LIGHT);
      return;
    }

    this.removeAttribute("data-surface");
    this.style.removeProperty("--appbar-bg-color");
    this.style.removeProperty("--appbar-text-color");
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._applyColorSurface();
    this._applyBackgroundImage();
  }

  /** Sets background image CSS variable from `url`. */
  private _applyBackgroundImage(): void {
    const image = this.url ? `url(${this.url})` : "none";
    this.style.setProperty("--appbar-bg-image", image);
  }

  /** Updates host style variables when visual input properties change. */
  protected override updated(changedProperties: PropertyValues): void {
    if (changedProperties.has("color")) {
      this._applyColorSurface();
    }

    if (changedProperties.has("url")) {
      this._applyBackgroundImage();
    }
  }

  override render() {
    return html`
      ${this.url
        ? html`
            <div class="background" aria-hidden="true"></div>
            <div class="background-overlay" aria-hidden="true"></div>
          `
        : nothing}
      <div
        class="content"
        part="content"
        role="${this.banner ? "banner" : "group"}"
      >
        <div class="leading" part="leading">
          <slot name="button">
            <button
              type="button"
              class="leading-button"
              aria-label="${this.leadingLabel}"
            >
              <sky-icon class="icon" icon="mdi:menu"></sky-icon>
            </button>
          </slot>
        </div>
        <div class="title-area" part="title">
          <span class="title-text">${this.title}</span>
        </div>
        <div class="actions" part="actions">
          <slot></slot>
        </div>
      </div>
    `;
  }
}
